import React from 'react';
import InvoiceRow, { InvoiceRowProps } from './InvoiceRow';
import { gql, DocumentNode } from '@apollo/client';
import {
	PayInvoicesModalContactFragment,
	PayInvoicesModalContactForGetContractWhereFragment,
	InstallmentStatus,
	InvoiceStatus,
} from '#types';
import MuiModal, { MuiModalProps } from '../../../mui/MuiModal/MuiModal';
import { Divider, List } from '@mui/material';
import { Button } from '../../Button';
import { filterTypenameFromArray } from '../../../lib/helpers/apollo';
import { handleClose } from '../closeContractSignModalIfOpen';

// sometimes we need this on Invoices and sometimes on [Invoice!]
const payInvoicesModalInvoiceFragment = gql`
	fragment payInvoicesModalInvoice on Invoice {
		id
		status
		paymentInstallments {
			id
			status
		}
		...payInvoicesModalRowInvoice
	}
	${ InvoiceRow.fragments.payInvoicesModalRowInvoiceFragment }
`;

const payInvoicesModalContactFragment = gql`
	fragment payInvoicesModalContact on Contact {
		id
		invoices {
			...payInvoicesModalInvoice
		}
	}
	${ payInvoicesModalInvoiceFragment }
`;

// now for the Contract-specific contact type
const payInvoicesModalInvoiceForGetContractWhereFragment = gql`
	fragment payInvoicesModalInvoiceForGetContractWhere on InvoiceForGetContractWhere {
		id
		status
		paymentInstallments {
			id
			status
		}
		...payInvoicesModalRowInvoiceForGetContractWhere
	}
	${ InvoiceRow.fragments.payInvoicesModalRowInvoiceForGetContractWhereFragment }
`;

// So many different types of Invoice in our graphql layer!
const payInvoicesModalContactForGetContractWhereFragment = gql`
	fragment payInvoicesModalContactForGetContractWhere on ContactForGetContractWhere {
		id
		invoices {
			...payInvoicesModalInvoiceForGetContractWhere
		}
	}
	${ payInvoicesModalInvoiceForGetContractWhereFragment }
`;

export type PayInvoicesModalProps = {
	title?: string;
	contact?:
	| PayInvoicesModalContactFragment
	| PayInvoicesModalContactForGetContractWhereFragment
	| null;
	paymentText?: string;
	excludedDocumentId: string;
} & Pick<MuiModalProps['muiModalProps'], 'open' | 'onClose'> &
Pick<InvoiceRowProps, 'onNavigate'>;

type PayInvoicesModalPropsWithFragment = React.FC<PayInvoicesModalProps> & {
	fragments: {
		payInvoicesModalContactFragment: DocumentNode;
		payInvoicesModalInvoiceFragment: DocumentNode;
		payInvoicesModalInvoiceForGetContractWhereFragment: DocumentNode;
		payInvoicesModalContactForGetContractWhereFragment: DocumentNode;
	};
};

const PayInvoicesModal: PayInvoicesModalPropsWithFragment = ( {
	open,
	onClose,
	onNavigate,
	contact,
	paymentText,
	title,
	excludedDocumentId,
} ) => {
	if ( !contact?.invoices ) {
		return null;
	}
	const invoices = filterTypenameFromArray( contact.invoices ).filter(
		( invoice ) =>
			[ InvoiceStatus.Sent, InvoiceStatus.PartiallyPaid ].includes(
				invoice.status
			) &&
			invoice.paymentInstallments?.some(
				( installment ) => installment.status === InstallmentStatus.Unpaid
			)
	);
	return (
		<MuiModal
			muiModalProps={ { open, onClose: handleClose( onClose ) } }
			title={ `${ title }! Next up...` }
			description={ paymentText }
			controls={
				<Button variant='outlined' onClick={ handleClose( onClose ) }>
					Close
				</Button>
			}
		>
			<List disablePadding>
				{ invoices
					.filter( ( invoice ) => invoice.id !== excludedDocumentId )
					.map( ( invoice, index ) => (
						<React.Fragment key={ invoice.id }>
							<InvoiceRow
								invoice={ invoice }
								onNavigate={ handleClose( onNavigate ) }
							/>
							{ index < invoices.length - 1 ? <Divider /> : null }
						</React.Fragment>
					) ) }
			</List>
		</MuiModal>
	);
};

PayInvoicesModal.fragments = {
	payInvoicesModalContactFragment,
	payInvoicesModalInvoiceFragment,
	payInvoicesModalInvoiceForGetContractWhereFragment,
	payInvoicesModalContactForGetContractWhereFragment,
};

export default PayInvoicesModal;
